// Hors connexion dans la coque de l'application (carnet de chantier, incrément 6 — conception § 7.2 à 7.4) :
//  - bandeau permanent : « hors connexion » et, tant que la file n'est pas vide, le compteur « en attente d'envoi » (ou « refusées ») ;
//  - envoi automatique : à l'ouverture, au retour du réseau (événement online) et toutes les minutes tant qu'il reste des saisies ;
//  - déconnexion BLOQUÉE tant que la file n'est pas vide (se déconnecter efface les données du téléphone), avec une explication et
//    un choix explicite « Effacer quand même », confirmé une seconde fois.
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CloudUpload, WifiOff } from 'lucide-react';
import { api } from '../lib/api.ts';
import { avertissementDeconnexion, viderHorsLigne } from '../lib/horsLigne.ts';
import { envoiEnCours, lister, saisies, synchroniser, useFileEnvoi, viderFile } from '../lib/fileEnvoi.ts';
import { useEnLigne } from '../lib/preferences.ts';
import { Alerte, Bouton, Dialogue, useConfirmation, useNotifier } from '../components/ui.tsx';

export const URL_ENVOIS = '/interventions/envois';
const PERIODE_MS = 60_000;
/** Requêtes à rafraîchir quand des saisies viennent d'arriver sur le serveur. */
const CLES = ['ma-journee', 'chantier', 'chantier-jour', 'chantier-reserves', 'chantier-pieces', 'chantier-avancement', 'planning-chantier', 'equipement', 'carnet',
  'bon', 'bons'];

/** Envoi automatique de la file du compte connecté (ouverture, retour du réseau, chaque minute s'il reste des saisies). */
export function useEnvoiAutomatique() {
  const { proprio } = useFileEnvoi();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const ref = useRef({ qc, notifier });
  ref.current = { qc, notifier };
  useEffect(() => {
    let vivant = true;
    const lancer = async () => {
      // Un envoi déjà lancé par un volet de saisie rend lui-même compte : on ne double pas ses messages.
      if (!navigator.onLine || envoiEnCours()) return;
      const n = (await lister(proprio).catch(() => [])).filter((o) => o.etat === 'en_attente').length;
      if (!n || !vivant) return;
      const b = await synchroniser(proprio).catch(() => null);
      if (!b || !vivant) return;
      if (b.envoyees) {
        await Promise.all(CLES.map((k) => ref.current.qc.invalidateQueries({ queryKey: [k] })));
        ref.current.notifier(`${saisies(b.envoyees)} envoyée${b.envoyees > 1 ? 's' : ''} au serveur.`);
        for (const a of b.avertissements.slice(0, 2)) ref.current.notifier(a, true);
      }
      if (b.refusees) ref.current.notifier(`${saisies(b.refusees)} refusée${b.refusees > 1 ? 's' : ''} : motif et correction dans « Envois ».`, true);
    };
    lancer();
    window.addEventListener('online', lancer);
    const t = setInterval(lancer, PERIODE_MS);
    return () => { vivant = false; window.removeEventListener('online', lancer); clearInterval(t); };
  }, [proprio]);
}

/** Bandeau sous la barre supérieure : hors connexion, saisies en attente d'envoi, envois refusés. */
export function BandeauConnexion() {
  const enLigne = useEnLigne();
  const { proprio, enAttente, refusees } = useFileEnvoi();
  const [envoi, setEnvoi] = useState(false);
  const n = enAttente.length;
  // Sur l'écran « Envois » lui-même, le lien « Voir » ne sert à rien : seul l'état hors connexion reste signalé.
  const surEnvois = useLocation().pathname === URL_ENVOIS;
  const voir = (texte: string) => (surEnvois ? null : <Link to={URL_ENVOIS} className="btn btn-compact btn-fantome bandeau-action">{texte}</Link>);
  if (!enLigne) {
    return (
      <div className="bandeau-demo bandeau-hors-ligne" role="status">
        <WifiOff aria-hidden />
        <span className="bandeau-texte"><strong>Hors connexion</strong>{n ? <> · {n} en attente<span className="masque-etroit"> d’envoi</span></> : null}
          <span className="masque-etroit"> — vos saisies restent sur ce téléphone et partiront seules au retour du réseau.</span></span>
        {n + refusees.length > 0 && voir('Voir')}
      </div>
    );
  }
  if (surEnvois) return null;
  if (refusees.length) {
    return (
      <div className="bandeau-demo bandeau-refus" role="status">
        <AlertTriangle aria-hidden />
        <span className="bandeau-texte"><strong>{saisies(refusees.length)} refusée{refusees.length > 1 ? 's' : ''}</strong>
          <span className="masque-etroit"> — motif, correction ou abandon dans « Envois »</span>{n ? <> · {n} en attente</> : null}</span>
        {voir('Voir')}
      </div>
    );
  }
  if (n) {
    const envoyer = async () => { setEnvoi(true); try { await synchroniser(proprio); } finally { setEnvoi(false); } };
    return (
      <div className="bandeau-demo bandeau-hors-ligne" role="status">
        <CloudUpload aria-hidden />
        <span className="bandeau-texte"><strong>{n} en attente d’envoi</strong><span className="masque-etroit"> — saisies faites sans réseau</span></span>
        <Bouton compact variante="fantome" className="bandeau-action" chargement={envoi} onClick={envoyer}>Envoyer</Bouton>
      </div>
    );
  }
  return null;
}

/**
 * Se déconnecter : file vide → déconnexion, effacement du cache hors connexion, des brouillons et de la file du compte ;
 * file non vide → BLOCAGE expliqué (« reconnectez-vous au réseau avant de vous déconnecter »), avec « Envoyer maintenant », et un
 * choix explicite « Effacer quand même », confirmé.
 */
export function useDeconnexion() {
  const { proprio } = useFileEnvoi();
  const qc = useQueryClient();
  const nav = useNavigate();
  const confirmation = useConfirmation();
  const enLigne = useEnLigne();
  const [blocage, setBlocage] = useState<{ attente: number; refusees: number } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState<string | null>(null);

  const terminer = async () => {
    await api.post('/api/auth/deconnexion').catch(() => {});
    await viderHorsLigne().catch(() => {});
    await viderFile(proprio).catch(() => {});
    qc.clear();
    nav('/connexion');
  };
  const compter = async () => {
    const l = await lister(proprio).catch(() => []);
    return { attente: l.filter((o) => o.etat === 'en_attente').length, refusees: l.filter((o) => o.etat === 'refusee').length };
  };
  const deconnecter = async () => {
    const c = await compter();
    if (c.attente + c.refusees > 0) { setResultat(null); setBlocage(c); return; }
    const avert = avertissementDeconnexion();
    if (avert && !(await confirmation.confirmer('Saisies non envoyées', avert, 'Me déconnecter quand même'))) return;
    await terminer();
  };
  const envoyerMaintenant = async () => {
    setEnvoi(true);
    try {
      const b = await synchroniser(proprio);
      const c = await compter();
      if (c.attente + c.refusees === 0) { setBlocage(null); await terminer(); return; }
      setBlocage(c);
      setResultat(b.arret === 'reseau' ? 'Toujours pas de réseau : rien n’est parti.'
        : b.arret ? 'Le serveur n’a pas tout reçu : réessayez dans un instant.'
          : c.refusees ? 'Des saisies ont été refusées : corrigez-les ou abandonnez-les dans « Envois ».' : null);
    } finally { setEnvoi(false); }
  };
  const effacer = async () => {
    const total = (blocage?.attente ?? 0) + (blocage?.refusees ?? 0);
    const ok = await confirmation.confirmer('Effacer les saisies non envoyées ?',
      `${saisies(total)} n’${total > 1 ? 'ont' : 'a'} jamais atteint le serveur : ${total > 1 ? 'elles seront perdues' : 'elle sera perdue'} définitivement (coches, consignes, photos…).`,
      'Effacer et me déconnecter');
    if (!ok) return;
    setBlocage(null);
    await terminer();
  };

  const total = blocage ? blocage.attente + blocage.refusees : 0;
  const element = (
    <>
      {blocage && (
        <Dialogue titre="Déconnexion bloquée" ouvert fermer={() => setBlocage(null)}
          pied={<>
            <Bouton onClick={() => setBlocage(null)}>Rester connecté</Bouton>
            <Bouton variante="principal" icone={<CloudUpload aria-hidden />} chargement={envoi} disabled={!enLigne} onClick={envoyerMaintenant}>Envoyer maintenant</Bouton>
          </>}>
          <div className="pile">
            <Alerte ton="ambre" titre={`${saisies(total)} pas encore envoyée${total > 1 ? 's' : ''}`}>
              {enLigne ? 'Envoyez-les avant de vous déconnecter.' : 'Reconnectez-vous au réseau avant de vous déconnecter.'} Se déconnecter efface les données de ce
              téléphone : {total > 1 ? 'elles seraient perdues' : 'elle serait perdue'}.
              {blocage.refusees > 0 && <> Dont {blocage.refusees} refusée{blocage.refusees > 1 ? 's' : ''} par le serveur : à corriger ou abandonner.</>}
            </Alerte>
            {resultat && <p className="petit" style={{ margin: 0 }}>{resultat}</p>}
            <Link to={URL_ENVOIS} className="btn" onClick={() => setBlocage(null)}>Voir les envois</Link>
            <Bouton variante="danger" onClick={effacer}>Effacer quand même…</Bouton>
          </div>
        </Dialogue>
      )}
      {confirmation.element}
    </>
  );
  return { deconnecter, element };
}
